import { useMemo } from 'react';
import { useCurrentUserStore } from '@/store/currentUserStore';
import { UserRole } from './types';

/**
 * Hook to access full permission checking capabilities
 * Returns all permission methods from the store
 */
export function usePermissions() {
  const hasPermission = useCurrentUserStore((state) => state.hasPermission);
  const hasAllPermissions = useCurrentUserStore((state) => state.hasAllPermissions);
  const hasRole = useCurrentUserStore((state) => state.hasRole);
  const hasAnyRole = useCurrentUserStore((state) => state.hasAnyRole);
  const canAccessFeature = useCurrentUserStore((state) => state.canAccessFeature);
  const user = useCurrentUserStore((state) => state.user);

  return useMemo(
    () => ({
      hasPermission,
      hasAllPermissions,
      hasRole,
      hasAnyRole,
      canAccessFeature,
      isAuthenticated: !!user,
      user,
    }),
    [hasPermission, hasAllPermissions, hasRole, hasAnyRole, canAccessFeature, user]
  );
}

/**
 * Hook to check if user has specific permission(s)
 * Returns boolean indicating if user has ANY of the specified roles (OR logic)
 *
 * @param roles - Array of roles to check
 * @param requireAll - If true, user must have ALL roles (AND logic)
 */
export function useHasPermission(roles: UserRole[], requireAll = false): boolean {
  const hasPermission = useCurrentUserStore((state) => state.hasPermission);
  const hasAllPermissions = useCurrentUserStore((state) => state.hasAllPermissions);
  const user = useCurrentUserStore((state) => state.user);

  return useMemo(() => {
    // If no roles specified, allow access to all authenticated users
    if (!roles || roles.length === 0) {
      return true;
    }

    // If user not loaded yet, deny access
    if (!user) {
      return false;
    }

    if (requireAll) {
      return hasAllPermissions(roles);
    }
    return hasPermission(roles);
  }, [roles, requireAll, hasPermission, hasAllPermissions, user]);
}

/**
 * Hook to check if user has a specific role
 */
export function useHasRole(role: UserRole): boolean {
  const hasRole = useCurrentUserStore((state) => state.hasRole);
  return useMemo(() => hasRole(role), [role, hasRole]);
}

/**
 * Hook to check if user can access a specific feature
 * Uses feature-to-roles mapping from PermissionService
 */
export function useCanAccessFeature(feature: string): boolean {
  const canAccessFeature = useCurrentUserStore((state) => state.canAccessFeature);
  return useMemo(() => canAccessFeature(feature), [feature, canAccessFeature]);
}

/**
 * Hook to get user's current roles
 */
export function useUserRoles(): UserRole[] {
  const user = useCurrentUserStore((state) => state.user);
  return useMemo(() => user?.roleCodes || [], [user]);
}

/**
 * Hook to check if user is authenticated
 */
export function useIsAuthenticated(): boolean {
  const user = useCurrentUserStore((state) => state.user);
  return useMemo(() => !!user, [user]);
}
